import React, { useEffect, useMemo, useRef, useState } from 'react';
import FilterBar from './components/FilterBar';
import StreamGrid from './components/StreamGrid';
import PaginationControls from './components/PaginationControls';
import { listDevices } from '@/api/deviceService';
import type { DeviceEntity } from '@/interface/responses/deviceResponse';
import type { DeviceListQueryWithFilters } from '@/api/deviceService';

// Live source: MediaMTX (WHEP over WebRTC)
import { mediamtxService } from '@/mediaservices/mediamtxService';
// Live source: Mediasoup (WebRTC MediaStream)
import { mediasoupService } from '@/mediaservices/mediasoupService';
import Icon, { ReloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';

type GridItem = {
  id: string | number;
  deviceName?: string | null; // API: device_serial
  device_name?: string | null; // API: device_name
  location?: string | null;
  deviceType: 'STB' | 'phone';
  status: 'live' | 'offline' | 'ready';
  whepUrl?: string | null;          // MediaMTX
  mediaStream?: MediaStream | null; // Mediasoup
  hasVideo?: boolean;
  hasAudio?: boolean;
  // Add network info for StreamCard
  networkInterface?: string;
  networkQuality?: string;
  wifiConnected?: boolean;
  ethernetConnected?: boolean;
  simConnected?: boolean;
  // ✅ ADD: Mute state properties
  muteState?: {
    selfMuted: boolean;
    adminMuted: boolean;
    effectivelyMuted: boolean;
  };
};

//   Add interface for component props
interface StreamManagementGridProps {
  excludeIds?: string[]; // Array of stream IDs to exclude from grid
}

const typeMap: Record<string, 'STB' | 'phone'> = { STB: 'STB', Mobile: 'phone' };
const statusMap: Record<string, 'live' | 'offline' | 'ready'> = {
  active: 'live',
  inactive: 'offline',
  idle: 'ready',
  error: 'offline',
};

const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === 'true';

const StreamManagementGrid: React.FC<StreamManagementGridProps> = ({ 
  excludeIds = [] 
}) => {
  // ---------------------- REST (Devices API) with Backend Filtering ----------------------
  const [devices, setDevices] = useState<DeviceEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ---------------------- Backend-driven filters ----------------------
  const [filters, setFilters] = useState({
    searchTerm: '',
    deviceGroup: [] as string[],
    connectionTypes: [] as string[],
    connectionStrengths: [] as string[],
    // Remove client-side only filters
    bandwidthMin: '',
    bandwidthMax: '',
    streamType: 'all',
    deviceType: undefined,
  });

  // ---------------------- MediaMTX and Mediasoup overlays ----------------------
  const [wsConnected, setWsConnected] = useState<boolean>(false);
  const [liveBySerial, setLiveBySerial] = useState<Map<string, { id: string; whepUrl: string; meta?: any }>>(new Map());
  const idToSerial = useRef<Map<string, string>>(new Map());
  
  //   Single declaration of mediasoupBySerial
  const [mediasoupBySerial, setMediasoupBySerial] = useState<
    Map<string, { mediaStream: MediaStream; hasVideo?: boolean; hasAudio?: boolean }>
  >(new Map());

  // ✅ ADD: Track mute states from WebSocket
  const [muteStates, setMuteStates] = useState<Map<string, { selfMuted: boolean; adminMuted: boolean; effectivelyMuted: boolean }>>(new Map());

  // Add state to track publishers info from stream_status_update
  const [publishersInfo, setPublishersInfo] = useState<Map<string, any>>(new Map());
  const [prevPublishersState, setPrevPublishersState] = useState<Set<string>>(new Set());

  // Convert UI filters to API query
  const buildApiQuery = (): DeviceListQueryWithFilters => {
    return {
      page: currentPage,
      limit: itemsPerPage,
      q: filters.searchTerm || undefined,
      deviceGroupIds: filters.deviceGroup
        .filter(g => g !== 'all')
        .map(g => parseInt(g))
        .filter(id => !isNaN(id)),
      network_interface: filters.connectionTypes.length > 0 
        ? filters.connectionTypes[0].toUpperCase() as 'WIFI' | 'ETHERNET' | 'SIM'
        : undefined,
      network_quality: filters.connectionStrengths.length > 0 
        ? filters.connectionStrengths[0].toUpperCase() as 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR'
        : undefined,
      device_type: filters.deviceType,
    };
  };

  const fetchPage = async () => {
    try {
      setLoading(true);
      const apiQuery = buildApiQuery();
      
      const res = await listDevices(apiQuery);
      setDevices(res.data ?? []);
      const apiTotal = Number(res.pagination?.totalRecords ?? 0);
      const apiPages = Number(res.pagination?.totalPages ?? 1);
      setTotalItems(Number.isFinite(apiTotal) ? apiTotal : (res.data?.length ?? 0));
      setTotalPages(Number.isFinite(apiPages) && apiPages > 0 ? apiPages : 1);
    } catch (e) {
      console.error('Failed to load devices:', e);
      setDevices([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  // ---------------------- Auto-reconnect WebSocket on app restart ----------------------
  // Check WebSocket connection status on mount
  useEffect(() => {
    if (!USE_MEDIAMTX) return;

    // Check connection status and update UI
    const checkStatus = () => {
      const connected = mediamtxService.isConnected();
      console.log('📊 WebSocket status:', connected ? 'Connected' : 'Disconnected');
      setWsConnected(connected);
    };

    // Check immediately
    checkStatus();

    // Set up a timer to periodically check connection status
    const statusInterval = setInterval(checkStatus, 2000);

    return () => {
      clearInterval(statusInterval);
    };
  }, []); // Run only once on mount

  // ---------------------- MediaMTX overlay ----------------------
  // Only attach MediaMTX listeners when USE_MEDIAMTX = true
  useEffect(() => { 
    if (!USE_MEDIAMTX) return;

    let mounted = true;
    //Debounce mechanism to prevent rapid duplicate calls
    let videoStartTimeout: NodeJS.Timeout | null = null;

    const getCurrentPublishersInfo = () => {
      const streams = mediamtxService.getStreamSummaries();
      if (streams.length > 0) {
        const publishersMap = new Map();
        streams.forEach(stream => {
          const serial = stream.meta?.deviceSerialNumber;
          if (serial) {
            publishersMap.set(serial, {
              streamId: stream.id,
              whepUrl: stream.whepUrl,
              deviceSerialNumber: serial,
              ...stream.meta
            });
          }
        });
        setPublishersInfo(publishersMap);
      }
    };
    // ✅ Get current state immediately when component mounts
    if (mediamtxService.isConnected()) {
      getCurrentPublishersInfo();
    }

    // ✅ Seed function to get current state on mount/connect
    const seed = () => {
      const list = mediamtxService.getStreamSummaries(); // [{ id, whepUrl, meta }]
      if (!list?.length) return;
      
      // ✅ Build publishers info from current streams
      const publishersMap = new Map();
      
      setLiveBySerial(prev => {
        const next = new Map(prev);
        list.forEach(s => {
          const serial = s.meta?.deviceSerialNumber;
          if (!serial) return;
          
          // Add to liveBySerial (for WHEP URLs)
          next.set(serial, { id: String(s.id), whepUrl: s.whepUrl, meta: s.meta });
          idToSerial.current.set(String(s.id), serial);
          
          // ✅ Add to publishersInfo (for status determination)
          publishersMap.set(serial, {
            streamId: s.id,
            whepUrl: s.whepUrl,
            deviceSerialNumber: serial,
            ...s.meta
          });
        });
        return next;
      });
      
      // ✅ Update publishersInfo state
      if (publishersMap.size > 0) {
        setPublishersInfo(publishersMap);
      }
    };

    // Listen for stream_status_update WebSocket event
    const onStreamStatusUpdate = (data: any) => {
      if (!mounted) return;
      
      console.log('📡✅ RECEIVED stream_status_update:', {
        timestamp: new Date().toISOString(),
        fullData: data,
        publishersInfo: data.publishersInfo ? {
          count: Object.keys(data.publishersInfo).length,
          devices: Object.keys(data.publishersInfo)
        } : 'null',
        muteStates: data.muteStates ? {
          count: Object.keys(data.muteStates).length,
          states: data.muteStates
        } : 'null'
      });
      
      // Update publishers info
      if (data.publishersInfo) {
        const publishersMap = new Map();
        const currentPublishers = new Set<string>();    
        Object.entries(data.publishersInfo).forEach(([key, value]) => {
          publishersMap.set(key, value);
          currentPublishers.add(key);
        });
        setPublishersInfo(publishersMap);
        
        setPrevPublishersState(prevState => {
          const newDevices: string[] = [];
          currentPublishers.forEach(deviceSerial => {
            if (!prevState.has(deviceSerial)) {
              const isVisibleOnCurrentPage = prevVisibleSerials.current.includes(deviceSerial);
              if (isVisibleOnCurrentPage) {
                newDevices.push(deviceSerial);
              }
            }
          });
          
          //Only send video_start if there are actually new devices
          if (newDevices.length > 0) {
            console.log('📺🔄 Triggering video_start for NEW devices from stream_status_update:', {
              newDevices,
              prevState: Array.from(prevState),
              currentPublishers: Array.from(currentPublishers)
            });
            
            //Clear any pending timeout
            if (videoStartTimeout) {
              clearTimeout(videoStartTimeout);
            }
            
            // Debounce to prevent rapid successive calls
            videoStartTimeout = setTimeout(() => {
              emitVideoStart(newDevices, [], 'device-appeared');
              videoStartTimeout = null;
            }, 100); // 100ms debounce
          }
          
          return currentPublishers;
        });
      }
      
      // Update mute states
      if (data.muteStates) {
        setMuteStates(new Map(Object.entries(data.muteStates)));
      }
    };

    const onSvcConnected = () => {
      if (mounted) {
        setWsConnected(true);
        setTimeout(() => {
          seed();
          const currentStreams = mediamtxService.getStreamSummaries();
          const initialPublishers = new Set<string>();
          currentStreams.forEach(s => {
            const serial = s.meta?.deviceSerialNumber;
            if (serial) initialPublishers.add(serial);
          });
          setPrevPublishersState(initialPublishers);
        }, 100);
      }
    };
    
    const onSvcDisconnected = () => mounted && setWsConnected(false);

    const onDeviceReconnecting = (data: any) => {
      if (!mounted) return;
      
      const deviceSerial = data.deviceSerialNumber;
      const reconnected = data.reconnected;
      
      // Only send video_start when device has successfully reconnected
      if (reconnected && deviceSerial) {
        // Check if this device is visible on current page
        const isVisibleOnCurrentPage = prevVisibleSerials.current.includes(deviceSerial);
        
        if (isVisibleOnCurrentPage) {
          // Clear any pending timeout for this device
          if (videoStartTimeout) {
            clearTimeout(videoStartTimeout);
          }
          
          // Small delay to ensure reconnection is stable
          videoStartTimeout = setTimeout(() => {
            emitVideoStart([deviceSerial], [], 'device-reconnected');
            videoStartTimeout = null;
          }, 500); // 500ms delay for stability
        }
      }
    };

    const onUpdate = (p: { id: string; whepUrl: string; meta?: any; online: boolean }) => {
      if (!mounted) return;
      const serial = p.meta?.deviceSerialNumber;
      if (!serial) return;
      
      setLiveBySerial(prev => {
        const next = new Map(prev);
        if (p.online) {
          next.set(serial, { id: String(p.id), whepUrl: p.whepUrl, meta: p.meta });
          idToSerial.current.set(String(p.id), serial);
        } else {
          next.delete(serial);
          idToSerial.current.delete(String(p.id));
        }
        return next;
      });
      
      // ✅ Keep publishersInfo in sync with stream updates
      setPublishersInfo(prev => {
        const next = new Map(prev);
        if (p.online) {
          next.set(serial, {
            streamId: p.id,
            whepUrl: p.whepUrl,
            deviceSerialNumber: serial,
            ...p.meta
          });
        } else {
          next.delete(serial);
        }
        return next;
      });
    };

    const onRemoved = (p: { id: string }) => {
      if (!mounted) return;
      const serial = idToSerial.current.get(String(p.id));
      
      setLiveBySerial(prev => {
        const next = new Map(prev);
        if (serial) next.delete(serial);
        return next;
      });
      
      // ✅ Remove from publishersInfo too
      if (serial) {
        setPublishersInfo(prev => {
          const next = new Map(prev);
          next.delete(serial);
          return next;
        });
        idToSerial.current.delete(String(p.id));
      }
    };

    mediamtxService.on('connected', onSvcConnected);
    mediamtxService.on('disconnected', onSvcDisconnected);
    mediamtxService.on('streamStatusUpdate', onStreamStatusUpdate);
    mediamtxService.on('streamUpdate', onUpdate);
    mediamtxService.on('streamRemoved', onRemoved);
    mediamtxService.on('device_reconnecting', onDeviceReconnecting); // ✅ ADD this listener

    if (mediamtxService.isConnected()) {
      setWsConnected(true);
      seed();
      setTimeout(() => {
        const currentStreams = mediamtxService.getStreamSummaries();
        const initialPublishers = new Set<string>();
        currentStreams.forEach(s => {
          const serial = s.meta?.deviceSerialNumber;
          if (serial) initialPublishers.add(serial);
        });
        setPrevPublishersState(initialPublishers);
      }, 200);
    }

    mediamtxService.on('streamUpdate', onUpdate);
    mediamtxService.on('streamRemoved', onRemoved);

    return () => {
      mounted = false;
      if (videoStartTimeout) {
        clearTimeout(videoStartTimeout);
      }
      mediamtxService.off('connected', onSvcConnected);
      mediamtxService.off('disconnected', onSvcDisconnected);
      mediamtxService.off('streamStatusUpdate', onStreamStatusUpdate);
      mediamtxService.off('streamUpdate', onUpdate);
      mediamtxService.off('streamRemoved', onRemoved);
      mediamtxService.off('device_reconnecting', onDeviceReconnecting); // ✅ ADD cleanup
    };
  }, []);

  // ---------------------- Mediasoup overlay ----------------------
  // Only attach Mediasoup listeners when USE_MEDIAMTX = false
  useEffect(() => {
    if (USE_MEDIAMTX) return;

    let mounted = true;

    // Ensure it connects (no-op if already initialized)
    mediasoupService.initialize().catch((e) => console.error('Mediasoup init failed', e));

    const onStreamUpdate = (p: {
      id: string;                    // streamId (device_id == device_serial)
      mediaStream: MediaStream;
      hasVideo?: boolean;
      hasAudio?: boolean;
      online?: boolean;
    }) => {
      if (!mounted) return;
      
      if (p.online !== false && p.mediaStream) {
        const serial = String(p.id);
        setMediasoupBySerial(prev => {
          const next = new Map(prev);
          next.set(serial, { 
            mediaStream: p.mediaStream, 
            hasVideo: p.hasVideo, 
            hasAudio: p.hasAudio 
          });
          return next;
        });
      }
    };

    const onStreamRemoved = (p: { id: string }) => {
      if (!mounted) return;
      const serial = String(p.id);
      setMediasoupBySerial(prev => {
        const next = new Map(prev);
        next.delete(serial);
        return next;
      });
    };

    mediasoupService.on('streamUpdate', onStreamUpdate);
    mediasoupService.on('streamRemoved', onStreamRemoved);

    // Seed any already available streams using the new method
    const existingStreams = mediasoupService.getStreamSummaries();
    existingStreams.forEach((summary) => {
      if (summary.mediaStream) {
        onStreamUpdate({
          id: summary.id,
          mediaStream: summary.mediaStream,
          hasVideo: summary.meta?.hasVideo,
          hasAudio: summary.meta?.hasAudio,
          online: true
        });
      }
    });

    return () => {
      mounted = false;
      mediasoupService.off('streamUpdate', onStreamUpdate);
      mediasoupService.off('streamRemoved', onStreamRemoved);
    };
  }, []);

  // ---------------------- Updated filter handlers ----------------------
  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    // Don't auto-fetch here, wait for Apply button
  };

  const handleApplyFilters = () => {
    console.log('Applying filters:', filters);
    setCurrentPage(1); // Reset to first page when applying filters
    // fetchPage will be called via useEffect when currentPage changes
    if (currentPage === 1) {
      fetchPage(); // If already on page 1, manually trigger fetch
    }
  };

  const handleResetFilters = async () => {
    const resetFilters = {
      searchTerm: '',
      deviceGroup: [],
      connectionTypes: [],
      connectionStrengths: [],
      bandwidthMin: '',
      bandwidthMax: '',
      streamType: 'all',
      deviceType: undefined,
    };
    setFilters(resetFilters);
    setCurrentPage(1);
    try {
      setLoading(true);
      const apiQuery = {
        page: 1,
        limit: itemsPerPage,
        q: undefined,
        deviceGroupIds: [],
        network_interface: undefined,
        network_quality: undefined,
        device_type: undefined,
      };
      
      const res = await listDevices(apiQuery);
      setDevices(res.data ?? []);
      const apiTotal = Number(res.pagination?.totalRecords ?? 0);
      const apiPages = Number(res.pagination?.totalPages ?? 1);
      setTotalItems(Number.isFinite(apiTotal) ? apiTotal : (res.data?.length ?? 0));
      setTotalPages(Number.isFinite(apiPages) && apiPages > 0 ? apiPages : 1);
      
    } catch (e) {
      console.error('Failed to load devices:', e);
      setDevices([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------- Build cards: API base + active overlay ----------------------
  const items: GridItem[] = useMemo(() => {
    let rows = devices.map<GridItem>((d) => {
      const serial = d.device_serial;

      const muteState = muteStates.get(serial) || {
        selfMuted: false,
        adminMuted: false,
        effectivelyMuted: false
      };

      if (USE_MEDIAMTX) {
        const mtx = liveBySerial.get(serial);
        
        // ✅ NEW: Check if device is in publishersInfo from stream_status_update
        const isInPublishersInfo = publishersInfo.has(serial);
        
        const status: GridItem['status'] = isInPublishersInfo ? 'live' : 'offline';

        return {
          id: d.id,
          deviceName: serial,
          device_name: d.device_name,
          location: d.location,
          deviceType: typeMap[d.device_type] || 'STB',
          status,
          whepUrl: mtx ? mtx.whepUrl : null, // Keep WHEP URL from liveBySerial
          mediaStream: null,
          networkInterface: d.latest_heartbeat?.network_interface,
          networkQuality: d.latest_heartbeat?.network_quality,
          wifiConnected: d.latest_heartbeat?.wifi_connected,
          ethernetConnected: d.latest_heartbeat?.ethernet_connected,
          simConnected: d.latest_heartbeat?.sim_data_connected,
          muteState,
        };
      } else {
        // Mediasoup logic remains unchanged
        const ms = mediasoupBySerial.get(serial);
        const status: GridItem['status'] =
          ms ? 'live' :
          (d.stream?.status ? statusMap[d.stream.status] || 'offline' : 'offline');

        return {
          id: d.id,
          deviceName: serial,
          device_name: d.device_name,
          location: d.location,
          deviceType: typeMap[d.device_type] || 'STB',
          status,
          mediaStream: ms?.mediaStream || null,
          whepUrl: null,
          networkInterface: d.latest_heartbeat?.network_interface,
          networkQuality: d.latest_heartbeat?.network_quality,
          wifiConnected: d.latest_heartbeat?.wifi_connected,
          ethernetConnected: d.latest_heartbeat?.ethernet_connected,
          simConnected: d.latest_heartbeat?.sim_data_connected,
          muteState,
        };
      }
    });

    //   Filter out excluded IDs
    if (excludeIds.length > 0) {
      rows = rows.filter(row => {
        if (USE_MEDIAMTX) {
          const mtx = liveBySerial.get(row.deviceName || '');
          return !excludeIds.includes(mtx?.id || '');
        } else {
          return !excludeIds.includes(row.deviceName || '');
        }
      });
    }

    return rows;
  }, [devices, liveBySerial, mediasoupBySerial, excludeIds, muteStates, publishersInfo]); // Add publishersInfo dependency

  // ---------------------- Actions from each card (unchanged) ----------------------
  const handleStreamAction = async (id: string | number, action: string) => {
    // Map the card's DB id -> device_serial (we need serial for both stacks)
    const dev = devices.find(d => String(d.id) === String(id));
    const serial = dev?.device_serial;
    if (!serial) {
      console.warn('[handleStreamAction] device not found for card id:', id);
      return;
    }

    try {
      if (USE_MEDIAMTX) {
        // MediaMTX path uses streamId (from WS overlay)
        const live = liveBySerial.get(serial);
        const streamId = live?.id; // streamId that WS gave us
        if (!streamId) {
          console.warn('[MediaMTX] No live streamId for serial', serial);
          return;
        }

        switch (action) {
          case 'make-host':
            mediamtxService.setHost(streamId);
            break;
          case 'make-cohost':
            mediamtxService.setCoHost(streamId);
            break;
          case 'mute':
            mediamtxService.setMicMuted(streamId, true);
            break;
          case 'unmute':
            mediamtxService.setMicMuted(streamId, false);
            break;
          default:
            console.warn('[MediaMTX] Unknown action', action);
        }
      } else {
        // Mediasoup path uses deviceId == device_serial
        switch (action) {
          case 'make-host':
            mediasoupService.setHost(serial); // Updated to use new method
            break;
          case 'make-cohost':
            mediasoupService.setCoHost(serial); // Updated to use new method
            break;
          case 'mute':
            mediasoupService.setMicMuted(serial, true); // New method
            break;
          case 'unmute':
            mediasoupService.setMicMuted(serial, false); // New method
            break;
          default:
            console.warn('[Mediasoup] action not implemented:', action);
        }
      }
    } catch (err) {
      console.error(`[handleStreamAction] ${action} failed for ${serial}`, err);
    }
  };

  const prevVisibleSerials = useRef<string[]>([]);

  const emitVideoStart = (devicesToStart: string[], devicesToStop: string[] = [], reason = 'visible-devices') => {
    if (!mediamtxService.isConnected()) {
      console.warn('📺❌ Cannot send video_start - MediaMTX not connected');
      return;
    }
    
    console.log('📺✅ SENDING video_start:', {
      devicesToStart,
      devicesToStop,
      reason,
      timestamp: new Date().toISOString(),
      connectedDevicesCount: devicesToStart.length,
      wsConnected: mediamtxService.isConnected()
    });
    
    mediamtxService.videoStart(devicesToStart, devicesToStop, reason);
  };

  useEffect(() => {
    if (!USE_MEDIAMTX) return;

    // Get all serials on the current page
    const visibleSerials = devices.map(d => d.device_serial).filter(Boolean);

    // Check if the visible devices have changed
    const serialsChanged = JSON.stringify(visibleSerials) !== JSON.stringify(prevVisibleSerials.current);

    console.log('📄 Page devices check:', {
      visibleSerials,
      prevVisibleSerials: prevVisibleSerials.current,
      serialsChanged,
      devicesCount: devices.length
    });

    if (serialsChanged) {
      //TODO: check the logic
      //const previousSerials = prevVisibleSerials.current || [];
      const previousSerials = [];
      
      console.log('📄✅ Page changed - devices updated:', {
        visibleSerials,
        previousSerials,
        action: 'sending video_start for page change'
      });
      
      // Send videoStart to new page devices, videoStop to previous page devices
      emitVideoStart(visibleSerials, previousSerials, 'page-change');
      
      prevVisibleSerials.current = visibleSerials;
    }

  }, [devices]);

  // ---------------------- MediaMTX specific effects ----------------------
  // Only run this effect if MediaMTX is used
  useEffect(() => {
    if (!USE_MEDIAMTX) return;

    let mounted = true;

    //Listen for mute state updates in the MediaMTX section
    const onStreamStatusUpdate = (data: any) => {
      if (!mounted || !data.muteStates) return;
      
      setMuteStates(new Map(Object.entries(data.muteStates)));
    };

    //Listen for direct mute updates
    const onMuteUpdate = (data: { id: string; muteState: any }) => {
      if (!mounted) return;
      
      setMuteStates(prev => {
        const next = new Map(prev);
        next.set(data.id, data.muteState);
        return next;
      });
    };

    mediamtxService.on('streamStatusUpdate', onStreamStatusUpdate);
    mediamtxService.on('mute', onMuteUpdate);

    return () => {
      mounted = false;
      mediamtxService.off('streamStatusUpdate', onStreamStatusUpdate);
      mediamtxService.off('mute', onMuteUpdate);
    };
  }, []);

  const handleReload = async () => {
    try {
      console.log('🔄 Manual reload triggered');
      await fetchPage();
      if (USE_MEDIAMTX && mediamtxService.isConnected()) {
        setTimeout(() => {
          const visibleSerials = devices.map(d => d.device_serial).filter(Boolean);
          if (visibleSerials.length > 0) {
            console.log('🔄✅ Manual reload - sending video_start:', {
              visibleSerials,
              deviceCount: visibleSerials.length
            });
            emitVideoStart(visibleSerials, [], 'reload');
            prevVisibleSerials.current = visibleSerials;
          } else {
            console.log('🔄❌ Manual reload - no visible serials found');
          }
        }, 100);
      } else {
        console.log('🔄❌ Manual reload - MediaMTX not connected or not using MediaMTX');
      }
    } catch (error) {
      console.error('Reload failed:', error);
    }
  };

  const handleDeviceReload = (deviceSerial: string) => {
    if (!USE_MEDIAMTX || !mediamtxService.isConnected()) {
      console.warn('MediaMTX not connected, cannot reload device:', deviceSerial);
      return;
    }
    
    console.log(`🔄 Sending individual video_start for device: ${deviceSerial}`);
    emitVideoStart([deviceSerial], [], 'individual-reload');
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-15">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">All Devices</h3>
            <Button
              onClick={handleReload}
              title="Refresh devices list"
            >
              <ReloadOutlined />
              Reload
            </Button>
          </div>

          <FilterBar
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onResetFilters={handleResetFilters}
            onApplyFilters={handleApplyFilters}
            streamCount={totalItems}
            excludedCount={excludeIds.length}
          />

          <StreamGrid
            items={items}
            onStreamAction={handleStreamAction}
            onDeviceReload={handleDeviceReload}
            loading={loading}
          />

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n: number) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>
      </main>
    </div>
  );
};

export default StreamManagementGrid;
